import { create, all } from "mathjs";
import { valueTypes } from "utils/builder/ResultClasses";
import DateError from "utils/customErrors/dateError";
import moment from 'moment';

function RoundUp(value) {
  return math.ceil(value);
}

function RoundDown(value) {
  return math.fix(value);
}

function Round(value) {
  return math.round(value);
}

function Min(...args) {
  const filtered = args.filter(v => v !== null && v !== undefined && v !== '');
  if (filtered.length === 0) return undefined; 
  if (filtered.length === 1) return filtered[0]; 
  return math.min(...filtered);
}

function Max(...args) {
  const filtered = args.filter(v => v !== null && v !== undefined && v !== '');
  if (filtered.length === 0) return undefined;
  if (filtered.length === 1) return filtered[0];
  return math.max(...filtered);
}


function SubtractingDates(...args) {
  const firstDate = new Date(args[0]);
  firstDate.setHours(0, 0, 0, 0);

  const secondDate = new Date(args[1]);
  secondDate.setHours(0, 0, 0, 0);

  const diffTime = secondDate.getTime() - firstDate.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (isNaN(diffDays)) {
    throw new DateError("Not valid dates");
  }

  return diffDays;
}

const math = create(all);

math.import({
  RoundUp,
  RoundDown,
  Round,
  Min,
  Max,
  SubtractingDates,
});

export class ComputeService {
  static executeFunction(props) {
    const { columns, rows, totals, conditionScheme } = props;

    const types = { ...props.types, Today: valueTypes.customDate };

    if (!conditionScheme?.columnCondition || !conditionScheme?.newColumnName) {
      return {
        columns,
        rows,
        totals,
        types,
      };
    }

    const {
      newColumnName: columnName,
      newColumnKey,
      columnCondition,
      variables,
    } = conditionScheme;
    const key = newColumnKey;
    
    let type;

    function determineResultType() {
      const variableNames = columnCondition.match(/\b\w+\b/g);
      let determinedType = null;

      if (variableNames && variableNames.length > 0) {
        for (const varName of variableNames) {
          const varType = types[varName];

          if (!determinedType) {
            determinedType = varType;
          } else if (varType && varType !== determinedType) {
            determinedType = 'number';
          }
        }
      }

      return determinedType === 12 ? 2 : determinedType;
    }

    type = determineResultType() || valueTypes.number;

    const newRows = rows.map((row, index) => {
      try {
        const mathScope = Array.isArray(variables)
          ? variables.reduce((res, { conditionKey, formulaKey }) => {
              let value = row[conditionKey];
              if (typeof value === 'string') {
                const date = new Date(value);
                value = isNaN(date.getTime()) ? value : date;
              }
              res[formulaKey] = value instanceof Date ? value.getTime() : value;
              return res;
            }, { Today: new Date().getTime() })
          : { ...variables, Today: new Date().getTime() };
          
        const isSubtractingDatesCondition = columnCondition.includes('SubtractingDates');
        const isTypeDate = (type === valueTypes.date || type === valueTypes.customDate);

        if (isSubtractingDatesCondition) {
          const variableNames = columnCondition.match(/\b\w+\b/g);
          const hasNullValues = variableNames.some(key => mathScope[key] === null);
          type = valueTypes.number

          if (hasNullValues) {
            return { ...row, [key]: '' };
          }
        }
        
        const value = math.evaluate(columnCondition, mathScope ?? {});
        
        let formattedValue = value;
        if (isSubtractingDatesCondition || (typeof value === 'number' && !isTypeDate)) {
          formattedValue = value;
        } else if (isTypeDate && typeof value === 'number') {
          formattedValue = value !== 0 ? moment(value).format('YYYY/MM/DD') : null;
        }
    
        if (index === 0 && (!type || isSubtractingDatesCondition)) {
          type = isSubtractingDatesCondition ? valueTypes.number : valueTypes[typeof value];
        }
        return { ...row, [key]: formattedValue };
      } catch (error) {
        if (index === 0 && !type) type = valueTypes.text;
        return {
          ...row,
          [key]: error.name !== "DateError" ? "Error" : "",
        };
      }
    });

    const newColumns = [
      ...columns,
      {
        id: newColumnKey,
        header: columnName,
        accessorKey: newColumnKey,
        formulaKey: newColumnKey,
        meta: {
          type: type,
          custom: true,
          summable: (type === valueTypes.date || type === valueTypes.customDate) ? false : true,
        },
      },
    ];

    return {
      columns: newColumns,
      rows: newRows,
      totals: totals,
      types: ComputeService.getTypes(newColumns),
    };
  }

  static executeFunctions({ columns, rows, columnSchemes }) {
    let executionResult = {
      columns,
      rows,
      types: ComputeService.getTypes(columns),
    };

    if (!columnSchemes) return executionResult;

    Object.keys(columnSchemes).forEach((key) => {

      const newData = ComputeService.executeFunction({
        ...executionResult,
        conditionScheme: columnSchemes[key],
      });

      executionResult = { ...newData };
    });

    return executionResult;
  }

  static getTypes(columns) {
    return columns.reduce(
      (res, { meta, formulaKey }) => ({ ...res, [formulaKey]: meta.type }),
      {}
    );
  }
}
